// src/pages/admin/UserInformation.js  (ปรับ path ตามโครงสร้างโปรเจกต์คุณได้เลย)

import React, { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../shared/auth/AuthProvider";
import { Spinner } from "react-bootstrap";

import "../../../shared/styles/pages/Account.css"

import { getFunctions, httpsCallable } from "firebase/functions";

function normalizeThaiPhone(phone) {
  const digits = phone.replace(/\D/g, "");

  if (digits.startsWith("0")) return "+66" + digits.slice(1);
  if (digits.startsWith("66")) return "+" + digits;

  return phone.trim();
}

const PAGE_SIZE = 50;

function formatThaiPhone(phone = "") {
  const raw = String(phone || "").trim();
  const digits = raw.replace(/\D/g, "");

  if (digits.startsWith("66") && digits.length >= 11) {
    return `0${digits.slice(2)}`;
  }

  if (digits.startsWith("0")) return digits;

  return raw;
}

function normalizePhoneSearch(value = "") {
  const digits = String(value || "").replace(/\D/g, "");

  if (digits.startsWith("0")) return `66${digits.slice(1)}`;
  if (digits.startsWith("66")) return digits;

  return digits;
}



function UserAvatarCircle({ u, size = 40 }) {
  const [imgError, setImgError] = useState(false);

  const initial =
    (u.displayName?.trim()?.[0] ??
      u.email?.trim()?.[0] ??
      "U").toUpperCase();

  const src = !imgError && u.photoURL ? u.photoURL : "";

  if (src) {
    return (
      <img
        src={src}
        alt={u.displayName || u.email}
        width={size}
        height={size}
        style={{
          borderRadius: "50%",
          objectFit: "cover",
          display: "block",
        }}
        onError={() => setImgError(true)}  // ถ้าโหลด fail ให้เปลี่ยนเป็นโหมดตัวอักษร
      />
    );
  }

  // fallback เป็นตัวอักษร
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        background: "#e9ecef",
        color: "#212529",
        fontWeight: 600,
        fontFamily: "Kanit, system-ui, -apple-system, Segoe UI, Roboto",
        userSelect: "none",
      }}
    >
      {initial}
    </div>
  );
}

export default function UserInformation() {
  const { user, profile, loading } = useAuth();
  const role = profile?.role || "user";

  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(true);

  // Search / Filter / Updating states
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatingId, setUpdatingId] = useState(null); // ใช้ร่วมทั้ง role & status

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchTerm, roleFilter, statusFilter]);

  // ดึง users realtime
  useEffect(() => {
    if (!user || role !== "admin") {
      setUsers([]);
      setFetching(false);
      return;
    }

    const q = query(collection(db, "users"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(list);
        setFetching(false);
      },
      (err) => {
        console.error("Error loading users:", err);
        setFetching(false);
      }
    );

    return () => unsub();
  }, [user, role]);

  // เปลี่ยน role
  const handleRoleChange = async (uid, newRole) => {
    if (uid === user.uid && newRole !== "admin") {
      alert("ไม่สามารถเปลี่ยน role ของบัญชีแอดมินตัวเองให้ออกจาก admin ได้");
      return;
    }

    const confirmText = `ยืนยันเปลี่ยนสิทธิ์ผู้ใช้งานคนนี้เป็น "${newRole}" หรือไม่?`;
    if (!window.confirm(confirmText)) return;

    try {
      setUpdatingId(uid);
      await updateDoc(doc(db, "users", uid), { role: newRole });
    } catch (err) {
      console.error("update role error:", err);
      alert("เกิดข้อผิดพลาดในการอัปเดต role");
    } finally {
      setUpdatingId(null);
    }
  };

  // เปลี่ยน status (active / blocked)
  const handleStatusChange = async (uid, newStatus) => {
    // กันไม่ให้บล็อกตัวเอง
    if (uid === user.uid && newStatus === "blocked") {
      alert("ไม่สามารถเปลี่ยนสถานะของบัญชีตัวเองเป็น blocked ได้");
      return;
    }

    const confirmText = `ยืนยันเปลี่ยนสถานะผู้ใช้งานคนนี้เป็น "${newStatus}" หรือไม่?`;
    if (!window.confirm(confirmText)) return;

    try {
      setUpdatingId(uid);
      await updateDoc(doc(db, "users", uid), { status: newStatus });
    } catch (err) {
      console.error("update status error:", err);
      alert("เกิดข้อผิดพลาดในการอัปเดตสถานะ");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAdminChangePhone = async (u) => {
    const provider = u.primaryProvider || u.provider;

    if (provider !== "phone") {
      alert("บัญชีนี้ไม่ได้ใช้เบอร์โทรศัพท์เป็นวิธีเข้าสู่ระบบหลัก");
      return;
    }

    const rawPhone = window.prompt(
      `เปลี่ยนเบอร์เข้าสู่ระบบของ ${
        u.displayName || u.email || u.id
      }\n\nเบอร์เดิม: ${
        u.phoneNumber || "-"
      }\n\nกรอกเบอร์ใหม่ เช่น 0801234567 หรือ +66801234567`
    );

    if (!rawPhone) return;

    const newPhoneNumber = normalizeThaiPhone(rawPhone);

    if (!/^\+\d{8,15}$/.test(newPhoneNumber)) {
      alert("รูปแบบเบอร์ไม่ถูกต้อง กรุณากรอกเช่น 0801234567 หรือ +66801234567");
      return;
    }

    const note = window.prompt(
      "บันทึกเหตุผล/หลักฐานที่ตรวจสอบแล้ว เช่น ตรวจสอบจากชื่อผู้สมัคร + สลิป + คอร์สที่ซื้อ",
      ""
    );

    const confirmText =
      `ยืนยันเปลี่ยนเบอร์เข้าสู่ระบบ?\n\n` +
      `ชื่อ: ${u.displayName || "-"}\n` +
      `UID: ${u.id}\n` +
      `เบอร์เดิม: ${u.phoneNumber || "-"}\n` +
      `เบอร์ใหม่: ${newPhoneNumber}\n\n` +
      `หลังจากเปลี่ยน ผู้ใช้ต้อง login ด้วยเบอร์ใหม่นี้`;

    if (!window.confirm(confirmText)) return;

    try {
      setUpdatingId(u.id);

      const functions = getFunctions(undefined, "asia-southeast1");
      const fn = httpsCallable(functions, "adminUpdateUserPhoneNumber");

      await fn({
        uid: u.id,
        newPhoneNumber,
        note: note || "",
      });

      alert("เปลี่ยนเบอร์เข้าสู่ระบบเรียบร้อยแล้ว");
    } catch (err) {
      console.error("admin change phone error:", err);
      alert(err?.message || "เกิดข้อผิดพลาดในการเปลี่ยนเบอร์");
    } finally {
      setUpdatingId(null);
    }
  };

  // กรอง users ตาม search + roleFilter + statusFilter
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredUsers = users.filter((u) => {
    const uRole = u.role || "user";
    const uStatus = u.status || "active";

    if (roleFilter !== "all" && uRole !== roleFilter) return false;
    if (statusFilter !== "all" && uStatus !== statusFilter) return false;

    if (!normalizedSearch) return true;

    const name = (u.displayName || "").toLowerCase();
    const email = (u.email || "").toLowerCase();
    const phoneRaw = String(u.phoneNumber || "").toLowerCase();
    const phoneThai = formatThaiPhone(u.phoneNumber).toLowerCase();

    const searchPhone = normalizePhoneSearch(normalizedSearch);
    const phoneRawDigits = normalizePhoneSearch(phoneRaw);
    const phoneThaiDigits = normalizePhoneSearch(phoneThai);

    return (
      name.includes(normalizedSearch) ||
      email.includes(normalizedSearch) ||
      phoneRaw.includes(normalizedSearch) ||
      phoneThai.includes(normalizedSearch) ||
      (!!searchPhone &&
        (phoneRawDigits.includes(searchPhone) ||
          phoneThaiDigits.includes(searchPhone)))
    );
  });

  const visibleUsers = filteredUsers.slice(0, visibleCount);
  const hasMoreUsers = visibleCount < filteredUsers.length;

  // -------- UI เช็คสิทธิ์ --------
  if (loading) {
    return (
      <div className="container py-5 text-center">
        <Spinner animation="border" size="sm" /> กำลังโหลด...
      </div>
    );
  }

  if (!user || role !== "admin") {
    return (
      <div className="container py-5">
        <h4 className="mb-3" style={{ fontFamily: "Kanit" }}>
          ไม่มีสิทธิ์เข้าถึงหน้านี้
        </h4>
        <p className="text-muted mb-0">
          หน้านี้เปิดให้เฉพาะผู้ดูแลระบบ (admin) เท่านั้น
        </p>
      </div>
    );
  }

  // -------- UI หลัก --------
  return (
    <div className="container py-4">
      <h3 style={{ fontFamily: "Kanit", marginBottom: "1.5rem" }}>
        ข้อมูลผู้ใช้งาน (Admin)
      </h3>

      {!fetching && (
        <div className="text-muted small mb-3">
          สมาชิกทั้งหมด {users.length.toLocaleString("th-TH")} คน
          {filteredUsers.length !== users.length
            ? ` · พบตามเงื่อนไข ${filteredUsers.length.toLocaleString("th-TH")} คน`
            : ""}
        </div>
      )}

      {/* Toolbar: Search + role filter + status filter */}
      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <input
            type="text"
            className="form-control"
            placeholder="ค้นหาด้วยชื่อ อีเมล หรือเบอร์โทร..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="col-md-3">
          <select
            className="form-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">ทุก role</option>
            <option value="user">user</option>
            <option value="creator">creator</option>
            <option value="admin">admin</option>
          </select>
        </div>

        <div className="col-md-3">
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">ทุกสถานะ</option>
            <option value="active">active</option>
            <option value="blocked">blocked</option>
          </select>
        </div>
      </div>

      {fetching && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" /> กำลังดึงข้อมูลผู้ใช้งาน...
        </div>
      )}

      {!fetching && filteredUsers.length === 0 && (
        <p className="text-muted">ไม่พบผู้ใช้งานที่ตรงกับเงื่อนไข</p>
      )}

      <div className="user-info-list">
        {visibleUsers.map((u) => {
          const uRole = u.role || "user";
          const uStatus = u.status || "active";

          return (
            <div
              key={u.id}
              className="d-flex align-items-center py-2 border-bottom"
            >
              {/* avatar */}
              <UserAvatarCircle u={u} size={40} />

              {/* ข้อมูลกลาง */}
              <div className="ms-3 flex-grow-1">
                <div
                  className="fw-semibold"
                  style={{ fontFamily: "Kanit" }}
                >
                  {u.displayName || u.email || "(ไม่มีชื่อ)"}
                  {u.id === user.uid && (
                    <span className="ms-2 badge bg-info text-dark">
                      บัญชีของคุณ
                    </span>
                  )}
                </div>

                {/* ✅ เพิ่มตรงนี้ */}
                <div
                  className="small text-muted"
                  style={{ fontSize: "0.75rem" }}
                >
                  UID: {u.id}
                </div>
                <div className="small text-muted">{u.email}</div>
                <div className="small text-muted">
                  Phone: {formatThaiPhone(u.phoneNumber) || "-"}
                </div>
                <div className="small mt-1">
                  <span className="badge bg-secondary me-2">
                    role: {uRole}
                  </span>
                  <span className="badge bg-light text-dark me-2">
                    status: {uStatus}
                  </span>
                  <span className="badge bg-light text-dark">
                    {u.emailVerified
                      ? "ยืนยันอีเมลแล้ว"
                      : "ยังไม่ยืนยันอีเมล"}
                  </span>
                </div>
              </div>

              {/* ฝั่งขวา: เปลี่ยน role + status */}
              <div className="d-flex flex-column align-items-end gap-1">
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: 120 }}
                  value={uRole}
                  onChange={(e) =>
                    handleRoleChange(u.id, e.target.value)
                  }
                  disabled={updatingId === u.id}
                >
                  <option value="user">user</option>
                  <option value="creator">creator</option>
                  <option value="admin">admin</option>
                </select>

                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: 120 }}
                  value={uStatus}
                  onChange={(e) =>
                    handleStatusChange(u.id, e.target.value)
                  }
                  disabled={updatingId === u.id}
                >
                  <option value="active">active</option>
                  <option value="blocked">blocked</option>
                </select>
                {(u.primaryProvider || u.provider) === "phone" && (
                  <button
                    type="button"
                    className="btn btn-outline-warning btn-sm"
                    style={{ minWidth: 120 }}
                    onClick={() => handleAdminChangePhone(u)}
                    disabled={updatingId === u.id}
                  >
                    เปลี่ยนเบอร์
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {hasMoreUsers ? (
        <div className="text-center mt-3">
          <button
            type="button"
            className="btn btn-outline-dark btn-sm"
            onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
          >
            แสดงเพิ่มเติม
          </button>
        </div>
      ) : null}
    </div>
  );
}

